import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth, useAccess } from '../../context/AuthContext';
import { useRex, useRexScreen } from '../../context/RexContext';
import { rexApplyTaxonomy, AiUnavailableError } from '../../lib/ai';
import TaxonomyTree from '../../components/TaxonomyTree';
import Badge from '../../components/Badge';
import Loader from '../../components/Loader';
import {
  buildTree,
  flattenTree,
  LEVEL_LABELS,
  LEVEL_BADGE_VARIANT,
  VERSION_STATUS_LABEL,
  cloneTaxonomyVersion,
  publishTaxonomyVersion,
  ensureOrgTaxonomyVersion,
  ensureDraftTaxonomyVersion,
  insertTaxonomyNodes,
} from '../../lib/taxonomy';

const BLANK_NODE = { code: '', name: '', description: '', platform_kb_ref: '' };

export default function ProcessTaxonomy() {
  const { profile, loading: authLoading } = useSupabaseAuth();
  const { isAdmin } = useAccess();
  const { messages, loading: rexLoading } = useRex();
  const orgId = profile?.organisation_id;

  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [deps, setDeps] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [editForm, setEditForm] = useState(BLANK_NODE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rexApplying, setRexApplying] = useState(false);
  const [showRexBanner, setShowRexBanner] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [newVersionLabel, setNewVersionLabel] = useState('');
  const [depForm, setDepForm] = useState({ predecessor_id: '', successor_id: '' });
  const [toast, setToast] = useState(null);
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [childForm, setChildForm] = useState(BLANK_NODE);
  const lastRexAssistant = useRef(0);

  const activeVersion = versions.find((v) => v.id === activeVersionId);
  const tree = useMemo(() => buildTree(nodes), [nodes]);
  const selected = nodes.find((n) => n.id === selectedId);
  const canEdit = isAdmin && activeVersion?.status === 'draft';
  const l34Nodes = nodes.filter((n) => n.level === 3 || n.level === 4);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadNodesForVersion = useCallback(async (versionId) => {
    if (!orgId || !versionId) {
      setNodes([]);
      setDeps([]);
      return;
    }
    const [{ data: nodeRows, error: nodeErr }, { data: depRows, error: depErr }] = await Promise.all([
      sb
        .from('org_process_hierarchy')
        .select('*')
        .eq('organisation_id', orgId)
        .eq('taxonomy_version_id', versionId)
        .order('level')
        .order('order_index'),
      sb
        .from('process_node_dependencies')
        .select('*')
        .eq('organisation_id', orgId)
        .eq('taxonomy_version_id', versionId),
    ]);
    if (nodeErr) throw nodeErr;
    if (depErr) throw depErr;
    setNodes(nodeRows || []);
    setDeps(depRows || []);
  }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId) {
      setVersions([]);
      setNodes([]);
      setDeps([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const versionRows = await ensureOrgTaxonomyVersion(sb, {
        organisationId: orgId,
        profileId: profile?.id,
      });
      setVersions(versionRows || []);

      let versionId = activeVersionId;
      if (!versionId && versionRows?.length) {
        const preferred = versionRows.find((v) => v.status === 'draft')
          || versionRows.find((v) => v.status === 'published')
          || versionRows[0];
        versionId = preferred.id;
        setActiveVersionId(versionId);
      }

      if (versionId) {
        await loadNodesForVersion(versionId);
      } else {
        setNodes([]);
        setDeps([]);
      }
    } catch (e) {
      console.error('[ProcessTaxonomy] load failed', e);
      showToast('Failed to load taxonomy');
    } finally {
      setLoading(false);
    }
  }, [orgId, profile?.id, activeVersionId, loadNodesForVersion]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  useEffect(() => {
    if (!orgId || !activeVersionId) return;
    loadNodesForVersion(activeVersionId).catch((e) => {
      console.error('[ProcessTaxonomy] version load failed', e);
      showToast('Failed to load taxonomy nodes');
    });
  }, [activeVersionId, orgId, loadNodesForVersion]);

  const applyRexTaxonomy = useCallback(async (proposalText, { quiet = false } = {}) => {
    if (!isAdmin || !orgId || !proposalText?.trim()) return false;
    setRexApplying(true);
    try {
      let versionId = activeVersionId;
      let version = versions.find((v) => v.id === versionId);
      if (!versionId || version?.status !== 'draft') {
        const sourceId = versionId || versions.find((v) => v.status === 'published')?.id || versions[0]?.id;
        if (!sourceId) {
          const ensured = await ensureOrgTaxonomyVersion(sb, { organisationId: orgId, profileId: profile?.id });
          if (!ensured.length) {
            const { data: draftVer, error } = await sb
              .from('taxonomy_versions')
              .insert({
                organisation_id: orgId,
                version_label: 'Rex draft',
                status: 'draft',
                created_by: profile?.id || null,
                notes: 'Created by Rex',
              })
              .select()
              .single();
            if (error) throw error;
            versionId = draftVer.id;
            version = draftVer;
          } else {
            const draftVer = await ensureDraftTaxonomyVersion(sb, {
              organisationId: orgId,
              sourceVersionId: ensured.find((v) => v.status === 'published')?.id || ensured[0].id,
              profileId: profile?.id,
              versionLabel: 'Rex draft',
            });
            versionId = draftVer.id;
            version = draftVer;
          }
        } else {
          const draftVer = await ensureDraftTaxonomyVersion(sb, {
            organisationId: orgId,
            sourceVersionId: sourceId,
            profileId: profile?.id,
            versionLabel: 'Rex draft',
          });
          versionId = draftVer.id;
          version = draftVer;
        }
        setActiveVersionId(versionId);
        const { data: refreshedVersions } = await sb
          .from('taxonomy_versions')
          .select('*')
          .eq('organisation_id', orgId)
          .order('created_at', { ascending: false });
        setVersions(refreshedVersions || []);
      }

      const currentNodes = nodes.length
        ? nodes
        : (await sb
          .from('org_process_hierarchy')
          .select('code, name, level')
          .eq('organisation_id', orgId)
          .eq('taxonomy_version_id', versionId)).data || [];

      const parsed = await rexApplyTaxonomy({
        proposal: proposalText,
        existing_nodes: currentNodes.map((n) => ({ code: n.code, name: n.name, level: n.level })),
      });

      const isFullDraft = /draft.*taxonomy|standard.*taxonomy|l1|full taxonomy/i.test(proposalText);
      const inserted = await insertTaxonomyNodes(sb, {
        organisationId: orgId,
        versionId,
        nodes: parsed.nodes,
        profileId: profile?.id,
        replace: isFullDraft && !currentNodes.length,
      });

      if (!inserted) {
        if (!quiet) showToast('Rex did not produce taxonomy nodes — try being more specific.');
        return false;
      }

      await loadNodesForVersion(versionId);
      setShowRexBanner(true);
      if (!quiet) showToast(`Rex added ${inserted} taxonomy node${inserted === 1 ? '' : 's'}`, 'success');
      return true;
    } catch (e) {
      const raw = e instanceof AiUnavailableError
        ? e.message
        : (e?.message || 'Could not apply Rex taxonomy draft');
      if (!quiet) showToast(raw);
      return false;
    } finally {
      setRexApplying(false);
    }
  }, [isAdmin, orgId, activeVersionId, versions, nodes, profile?.id, loadNodesForVersion]);

  const rexSnapshot = useMemo(() => ({
    organisationId: orgId,
    activeVersion: activeVersion ? {
      id: activeVersion.id,
      label: activeVersion.version_label,
      status: activeVersion.status,
    } : null,
    nodeCount: nodes.length,
    canEdit,
  }), [orgId, activeVersion, nodes.length, canEdit]);

  useRexScreen({
    snapshot: rexSnapshot,
    priming: isAdmin
      ? 'Describe your company process areas — I\'ll draft the L1–L4 taxonomy straight into the tree. You can edit anything before publishing.'
      : null,
  });

  useEffect(() => {
    if (!isAdmin || rexLoading || rexApplying) return;
    const assistantCount = messages.filter((m) => m.role === 'assistant').length;
    if (assistantCount === 0 || assistantCount === lastRexAssistant.current) return;
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return;
    lastRexAssistant.current = assistantCount;
    applyRexTaxonomy(last.content, { quiet: true });
  }, [messages, rexLoading, rexApplying, isAdmin, applyRexTaxonomy]);

  useEffect(() => {
    if (selected) {
      setEditForm({
        code: selected.code || '',
        name: selected.name || '',
        description: selected.description || '',
        platform_kb_ref: selected.platform_kb_ref || '',
      });
    }
  }, [selectedId, selected]);

  async function reloadNodes() {
    if (activeVersionId) await loadNodesForVersion(activeVersionId);
  }

  const nodeDeps = useMemo(() => {
    if (!selectedId) return { preds: [], succs: [] };
    const byId = new Map(nodes.map((n) => [n.id, n]));
    return {
      preds: deps
        .filter((d) => d.successor_id === selectedId)
        .map((d) => ({ ...d, predecessor: byId.get(d.predecessor_id) })),
      succs: deps
        .filter((d) => d.predecessor_id === selectedId)
        .map((d) => ({ ...d, successor: byId.get(d.successor_id) })),
    };
  }, [deps, selectedId, nodes]);

  async function saveNode() {
    if (!selectedId || !canEdit) return;
    setSaving(true);
    try {
      const { error } = await sb
        .from('org_process_hierarchy')
        .update({
          code: editForm.code.trim(),
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          platform_kb_ref: editForm.platform_kb_ref.trim() || null,
        })
        .eq('id', selectedId);
      if (error) throw error;
      await reloadNodes();
      showToast('Node saved', 'success');
    } catch (e) {
      console.error('[ProcessTaxonomy] save failed', e);
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function addChild() {
    if (!selected || !canEdit || selected.level >= 4) return;
    setSaving(true);
    try {
      const siblings = nodes.filter((n) => n.parent_id === selected.id);
      const { error } = await sb.from('org_process_hierarchy').insert({
        organisation_id: orgId,
        taxonomy_version_id: activeVersionId,
        parent_id: selected.id,
        level: selected.level + 1,
        code: childForm.code.trim(),
        name: childForm.name.trim(),
        description: childForm.description.trim() || null,
        platform_kb_ref: childForm.platform_kb_ref.trim() || null,
        order_index: siblings.length + 1,
        is_custom: true,
      });
      if (error) throw error;
      setAddChildOpen(false);
      setChildForm(BLANK_NODE);
      await reloadNodes();
      showToast('Child node added', 'success');
    } catch (e) {
      showToast(e.message || 'Could not add node');
    } finally {
      setSaving(false);
    }
  }

  async function addRootNode() {
    if (!canEdit) return;
    const code = prompt('L1 code (e.g. R2R):');
    const name = prompt('L1 name:');
    if (!code?.trim() || !name?.trim()) return;
    setSaving(true);
    try {
      const l1Count = nodes.filter((n) => n.level === 1).length;
      const { error } = await sb.from('org_process_hierarchy').insert({
        organisation_id: orgId,
        taxonomy_version_id: activeVersionId,
        level: 1,
        code: code.trim(),
        name: name.trim(),
        order_index: l1Count + 1,
        is_custom: true,
      });
      if (error) throw error;
      await reloadNodes();
      showToast('L1 node added', 'success');
    } catch (e) {
      showToast(e.message || 'Could not add node');
    } finally {
      setSaving(false);
    }
  }

  async function createDraft() {
    if (!isAdmin || !activeVersionId) return;
    const label = newVersionLabel.trim() || `v${versions.length + 1}.0`;
    setSaving(true);
    try {
      const ver = await cloneTaxonomyVersion(sb, {
        organisationId: orgId,
        sourceVersionId: activeVersionId,
        versionLabel: label,
        profileId: profile?.id,
      });
      setNewVersionLabel('');
      setShowVersions(false);
      await load();
      setActiveVersionId(ver.id);
      showToast(`Draft ${label} created`, 'success');
    } catch (e) {
      showToast(e.message || 'Could not create draft');
    } finally {
      setSaving(false);
    }
  }

  async function publishDraft() {
    if (!isAdmin || activeVersion?.status !== 'draft') return;
    if (!window.confirm(`Publish ${activeVersion.version_label}? This archives the current published version.`)) return;
    setSaving(true);
    try {
      await publishTaxonomyVersion(sb, {
        versionId: activeVersionId,
        organisationId: orgId,
        profileId: profile?.id,
      });
      await load();
      showToast('Version published', 'success');
    } catch (e) {
      showToast(e.message || 'Publish failed');
    } finally {
      setSaving(false);
    }
  }

  async function addDependency() {
    if (!canEdit || !depForm.predecessor_id || !depForm.successor_id) return;
    if (depForm.predecessor_id === depForm.successor_id) {
      showToast('Predecessor and successor must differ');
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb.from('process_node_dependencies').insert({
        organisation_id: orgId,
        taxonomy_version_id: activeVersionId,
        predecessor_id: depForm.predecessor_id,
        successor_id: depForm.successor_id,
        created_by: profile?.id || null,
      });
      if (error) throw error;
      setDepForm({ predecessor_id: '', successor_id: '' });
      await reloadNodes();
      showToast('Dependency added', 'success');
    } catch (e) {
      showToast(e.message || 'Could not add dependency');
    } finally {
      setSaving(false);
    }
  }

  async function removeDependency(id) {
    if (!canEdit) return;
    setSaving(true);
    try {
      const { error } = await sb.from('process_node_dependencies').delete().eq('id', id);
      if (error) throw error;
      await reloadNodes();
    } catch (e) {
      showToast(e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || (loading && !nodes.length && orgId)) {
    return <Loader text="Loading process taxonomy…" />;
  }

  if (!orgId) {
    return (
      <div className="tax-admin-main">
        <div className="empty" style={{ padding: 32 }}>
          No organisation linked to your profile — taxonomy data cannot be loaded.
        </div>
      </div>
    );
  }

  return (
    <div className="tax-admin-main">
      {showRexBanner && (
        <div className="rex-fill-banner" style={{ margin: '12px 16px 0' }}>
          <span>
            ✦ Rex has updated this taxonomy draft — review the tree, then publish when ready.
          </span>
          <button type="button" className="rex-fill-dismiss" onClick={() => setShowRexBanner(false)}>
            Dismiss
          </button>
        </div>
      )}
      {(rexApplying || rexLoading) && (
        <div className="rex-fill-banner loading" style={{ margin: '12px 16px 0' }}>
          <span>Rex is drafting your taxonomy…</span>
        </div>
      )}
      <div className="topbar">
          <div>
            <div className="tbtitle">Process taxonomy</div>
            <div className="tbcrumb">ADMIN-02 · Company process hierarchy (L1–L4)</div>
          </div>
          <div className="badges" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn-ghost" onClick={() => setShowVersions((v) => !v)}>
              {activeVersion?.version_label || 'Version'} · {VERSION_STATUS_LABEL[activeVersion?.status] || '—'} ▾
            </button>
            {canEdit && activeVersion?.status === 'draft' && (
              <button type="button" className="btn-primary" disabled={saving} onClick={publishDraft}>
                Publish {activeVersion.version_label}
              </button>
            )}
            {!isAdmin && <Badge variant="am">Read-only</Badge>}
          </div>
        </div>

        <div className="tax-admin-body">
          <div className="tax-panel">
            <div className="tax-panel-hdr">
              <span className="tax-panel-title">Process hierarchy</span>
              <span className="tax-panel-meta">{flattenTree(tree).length} nodes</span>
            </div>
            <input
              className="tax-search"
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TaxonomyTree
              tree={tree}
              selectedId={selectedId}
              onSelect={(n) => setSelectedId(n.id)}
              search={search}
              mode="browse"
            />
            {canEdit && (
              <div className="tax-panel-foot">
                <button type="button" className="btn-ghost" onClick={addRootNode}>+ Add L1</button>
              </div>
            )}
          </div>

          <div className="tax-detail-panel">
            {!selected ? (
              <div className="empty" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Select a node to view or edit details.
              </div>
            ) : (
              <>
                <div className="tax-detail-hdr">
                  <div>
                    <Badge variant={LEVEL_BADGE_VARIANT[selected.level]}>{LEVEL_LABELS[selected.level]}</Badge>
                    <span className="tax-detail-code">{selected.code}</span>
                    <div className="tax-detail-name">{selected.name}</div>
                    {selected.description && <div className="tax-detail-desc">{selected.description}</div>}
                  </div>
                  {canEdit && selected.level < 4 && (
                    <button type="button" className="btn-ghost" onClick={() => { setAddChildOpen(true); setChildForm(BLANK_NODE); }}>
                      + Add child
                    </button>
                  )}
                </div>

                <div className="tax-detail-body">
                  <div className="card" style={{ margin: '12px 16px' }}>
                    <div className="chdr"><span className="ctitle">Node properties</span></div>
                    <div className="form-2col" style={{ marginBottom: 10 }}>
                      <div className="form-row">
                        <label className="form-label">Code</label>
                        <input className="form-input" value={editForm.code} disabled={!canEdit}
                          onChange={(e) => setEditForm((p) => ({ ...p, code: e.target.value }))} />
                      </div>
                      <div className="form-row">
                        <label className="form-label">Name</label>
                        <input className="form-input" value={editForm.name} disabled={!canEdit}
                          onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-row" style={{ marginBottom: 10 }}>
                      <label className="form-label">Description</label>
                      <textarea className="form-textarea" rows={3} value={editForm.description} disabled={!canEdit}
                        onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
                    </div>
                    <div className="form-row">
                      <label className="form-label">Platform KB ref</label>
                      <input className="form-input" value={editForm.platform_kb_ref} disabled={!canEdit}
                        onChange={(e) => setEditForm((p) => ({ ...p, platform_kb_ref: e.target.value }))} />
                    </div>
                    {canEdit && (
                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" className="btn-primary" disabled={saving} onClick={saveNode}>Save</button>
                      </div>
                    )}
                    {!canEdit && activeVersion?.status !== 'draft' && isAdmin && (
                      <p style={{ marginTop: 10, fontSize: 11, color: '#888780' }}>
                        Published versions are read-only. Create a draft to edit nodes.
                      </p>
                    )}
                  </div>

                  {(selected.level === 3 || selected.level === 4) && (
                    <div className="card" style={{ margin: '0 16px 16px' }}>
                      <div className="chdr"><span className="ctitle">Node dependencies (DAG)</span></div>
                      <p style={{ fontSize: 11, color: '#888780', marginBottom: 10 }}>
                        L3/L4 sequencing edges for process design and Gantt auto-sequence.
                      </p>
                      {nodeDeps.preds.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: '#888780', textTransform: 'uppercase', marginBottom: 4 }}>Depends on</div>
                          {nodeDeps.preds.map((d) => (
                            <div key={d.id} className="ir">
                              <span>{d.predecessor?.code} — {d.predecessor?.name}</span>
                              {canEdit && (
                                <button type="button" className="row-act row-act-close" onClick={() => removeDependency(d.id)}>Remove</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {nodeDeps.succs.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: '#888780', textTransform: 'uppercase', marginBottom: 4 }}>Blocks</div>
                          {nodeDeps.succs.map((d) => (
                            <div key={d.id} className="ir">
                              <span>{d.successor?.code} — {d.successor?.name}</span>
                              {canEdit && (
                                <button type="button" className="row-act row-act-close" onClick={() => removeDependency(d.id)}>Remove</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {canEdit && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                          <div className="form-row">
                            <label className="form-label">Predecessor</label>
                            <select className="form-select" value={depForm.predecessor_id}
                              onChange={(e) => setDepForm((p) => ({ ...p, predecessor_id: e.target.value }))}>
                              <option value="">Select…</option>
                              {l34Nodes.map((n) => (
                                <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-row">
                            <label className="form-label">Successor</label>
                            <select className="form-select" value={depForm.successor_id}
                              onChange={(e) => setDepForm((p) => ({ ...p, successor_id: e.target.value }))}>
                              <option value="">Select…</option>
                              {l34Nodes.map((n) => (
                                <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                              ))}
                            </select>
                          </div>
                          <button type="button" className="btn-primary" disabled={saving} onClick={addDependency}>Add</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {showVersions && (
            <div className="slideover-overlay" onClick={() => setShowVersions(false)}>
              <div className="slideover" style={{ width: 360 }} onClick={(e) => e.stopPropagation()}>
                <div className="slideover-hdr">
                  <div>
                    <div className="tbtitle">Version history</div>
                    <div className="tbcrumb">{versions.length} version{versions.length !== 1 ? 's' : ''}</div>
                  </div>
                  <button type="button" className="slideover-icon-btn" onClick={() => setShowVersions(false)}>✕</button>
                </div>
                <div className="slideover-body">
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className={`sr-hist-item${v.id === activeVersionId ? ' active' : ''}`}
                      onClick={() => { setActiveVersionId(v.id); setSelectedId(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && setActiveVersionId(v.id)}
                      role="button"
                      tabIndex={0}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 500 }}>{v.version_label}</span>
                        <Badge variant={v.status === 'published' ? 'gr' : v.status === 'draft' ? 'pu' : ''}>
                          {VERSION_STATUS_LABEL[v.status]}
                        </Badge>
                      </div>
                      {v.published_at && (
                        <div style={{ fontSize: 10, color: '#888780', marginTop: 4 }}>
                          Published {new Date(v.published_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                  {isAdmin && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '0.5px solid #f1efe8' }}>
                      <div className="form-row" style={{ marginBottom: 8 }}>
                        <label className="form-label">New draft label</label>
                        <input className="form-input" placeholder="e.g. v1.1" value={newVersionLabel}
                          onChange={(e) => setNewVersionLabel(e.target.value)} />
                      </div>
                      <button type="button" className="btn-primary" style={{ width: '100%' }} disabled={saving} onClick={createDraft}>
                        Clone current → new draft
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {addChildOpen && selected && (
            <div className="modal-overlay" onClick={() => setAddChildOpen(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-hdr">
                  <span>Add L{selected.level + 1} under {selected.code}</span>
                  <button type="button" className="modal-close" onClick={() => setAddChildOpen(false)}>✕</button>
                </div>
                <div className="modal-body">
                  <div className="form-row">
                    <label className="form-label">Code</label>
                    <input className="form-input" value={childForm.code}
                      onChange={(e) => setChildForm((p) => ({ ...p, code: e.target.value }))} />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Name</label>
                    <input className="form-input" value={childForm.name}
                      onChange={(e) => setChildForm((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                </div>
                <div className="modal-ftr">
                  <button type="button" className="btn-ghost" onClick={() => setAddChildOpen(false)}>Cancel</button>
                  <button type="button" className="btn-primary" disabled={saving} onClick={addChild}>Add node</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {toast && (
          <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
        )}
    </div>
  );
}
